(() => {
  const listenerGuard = '__dripnordSizeMeasurementsDelegatedReady';
  if (window[listenerGuard]) return;

  window[listenerGuard] = true;
  window.dripnordSizeMeasurementsReady = true;

  const rootOpenClass = 'measurements-modal-open';
  const mobileModalQuery = '(max-width: 749px)';
  const reducedMotionQuery = '(prefers-reduced-motion: reduce)';
  const modalTransitionFallback = 240;
  const selectors = {
    wrapper: '[data-size-measurements], [data-dn-size-measurements]',
    modal: '[data-size-modal], [data-dn-size-modal]',
    open: '[data-size-modal-open], [data-dn-size-trigger]',
    close: '[data-size-modal-close], [data-dn-size-close]',
    overlay: '[data-size-modal-overlay], .dn-size-modal__overlay',
    dialog: '[data-size-modal-dialog], .dn-size-modal__dialog, [role="dialog"]',
    unit: 'button[data-size-unit], button[data-dn-size-unit]',
    tab: 'button[data-dn-size-tab]',
    panel: '[data-dn-size-panel]',
    contact: '[data-size-modal-contact], .dn-size-modal__contact a',
    measurements: '.dn-size-modal__measurements',
    measurementSource: 'template[data-dn-measurement-source]',
    sharedMeasurementSource: 'template[data-dn-shared-measurement-source]',
    defaultMeasurementSource: 'template[data-dn-default-measurement-source]',
    guides: '[data-dn-measurement-guides]',
    illustration: '[data-size-illustration], [data-dn-size-illustration]',
    howLayout: '[data-dn-how-layout]',
    explanations: '[data-dn-measurement-explanations]',
    explanation: '[data-dn-measurement-explanation]',
    notice: '[data-dn-size-notice]',
    overview: '[data-dn-product-overview]',
    review: '[data-dn-review-carousel]'
  };
  const openModalSelector = [
    '[data-size-modal].is-open',
    '[data-dn-size-modal].is-open',
    '[data-size-modal].is-transitioning',
    '[data-dn-size-modal].is-transitioning'
  ].join(',');

  const focusableSelector = [
    'a[href]',
    'area[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  let activeModal = null;
  let activeDialog = null;
  let activeTrigger = null;
  let backgroundElementsState = [];
  const openingFrames = new WeakMap();
  const transitionCleanups = new WeakMap();
  const defaultProductSourceRequests = new Map();

  function asElement(target) {
    if (!target) return null;
    if (target.nodeType === 1) return target;
    return target.parentElement || null;
  }

  function closestElement(target, selector) {
    return asElement(target)?.closest?.(selector) || null;
  }

  function getModalFromControl(control) {
    return control?.closest?.(selectors.modal) || getOpenModal();
  }

  function getOpenModal() {
    if (activeModal?.isConnected && !activeModal.hidden) return activeModal;
    return document.querySelector(openModalSelector);
  }

  function getDialog(modal) {
    return modal?.querySelector?.(selectors.dialog) || null;
  }

  function setActiveModalTab(modal, tabName, focusTab = false) {
    if (!modal || !tabName) return;

    let activeTab = null;
    modal.querySelectorAll(selectors.tab).forEach((tab) => {
      const isActive = tab.dataset.dnSizeTab === tabName;
      tab.classList.toggle('is-active', isActive);
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      tab.setAttribute('tabindex', isActive ? '0' : '-1');
      if (isActive) activeTab = tab;
    });

    modal.querySelectorAll(selectors.panel).forEach((panel) => {
      panel.hidden = panel.dataset.dnSizePanel !== tabName;
    });

    modal.dataset.dnActiveSizeTab = tabName;
    if (focusTab && activeTab) activeTab.focus({ preventScroll: true });
  }

  function getModalPortal() {
    let portal = document.querySelector('[data-dn-size-modal-portal]');
    if (portal) return portal;

    portal = document.createElement('div');
    portal.className = 'main-product dn-size-modal-portal';
    portal.setAttribute('data-dn-size-modal-portal', '');
    document.body.appendChild(portal);
    return portal;
  }

  function getModalTargetId(trigger) {
    const targetId = trigger?.dataset.sizeModalOpen || trigger?.getAttribute?.('aria-controls');
    return targetId && targetId !== 'true' ? targetId.replace(/^#/, '') : '';
  }

  function getModalForWrapper(wrapper) {
    if (!wrapper?.querySelector) return null;

    const trigger = wrapper.querySelector(selectors.open);
    const targetId = getModalTargetId(trigger);
    return (targetId ? document.getElementById(targetId) : null) ||
      wrapper.querySelector(selectors.modal);
  }

  function getModals(root = document) {
    const modals = [];

    if (root?.matches?.(selectors.modal)) modals.push(root);
    root?.querySelectorAll?.(selectors.modal).forEach((modal) => {
      if (!modals.includes(modal)) modals.push(modal);
    });

    return modals;
  }

  function mountModalInPortal(modal) {
    if (!modal || modal.closest('[data-dn-size-modal-portal]')) return;

    modal.hidden = false;
    modal.setAttribute('aria-hidden', 'true');
    modal.dataset.dnModalState = 'closed';
    getModalPortal().appendChild(modal);
  }

  function mountAllModals(root = document) {
    getModals(root).forEach(mountModalInPortal);
  }

  function removeEmptyModalPortal() {
    const portal = document.querySelector('[data-dn-size-modal-portal]');
    if (portal && !portal.children.length) portal.remove();
  }

  function removeModalForWrapper(wrapper) {
    const targetId = getModalTargetId(wrapper?.querySelector(selectors.open));
    const modals = getModals(document).filter((modal) => modal.id === targetId);
    modals.forEach((modal) => {
      if (modal === activeModal) closeModal(false, modal, true);
      modal.remove();
    });
    removeEmptyModalPortal();
  }

  function lockBackgroundScroll(modal) {
    if (!document.body) return;
    document.documentElement.classList.add(rootOpenClass);
    document.body.classList.add(rootOpenClass);

    const portal = modal?.closest?.('[data-dn-size-modal-portal]');
    backgroundElementsState = Array.from(document.body.children)
      .filter((element) => element !== portal)
      .map((element) => ({
        element,
        hadInert: element.hasAttribute('inert'),
        ariaHidden: element.getAttribute('aria-hidden')
      }));

    backgroundElementsState.forEach(({ element }) => {
      element.setAttribute('inert', '');
      element.setAttribute('aria-hidden', 'true');
    });
  }

  function unlockBackgroundScroll() {
    document.documentElement.classList.remove(rootOpenClass);
    document.body?.classList.remove(rootOpenClass);

    backgroundElementsState.forEach(({ element, hadInert, ariaHidden }) => {
      if (!element.isConnected) return;
      if (!hadInert) element.removeAttribute('inert');
      if (ariaHidden === null) {
        element.removeAttribute('aria-hidden');
      } else {
        element.setAttribute('aria-hidden', ariaHidden);
      }
    });
    backgroundElementsState = [];
  }

  function shouldAnimateModal() {
    return window.matchMedia?.(mobileModalQuery).matches &&
      !window.matchMedia?.(reducedMotionQuery).matches;
  }

  function cancelOpeningFrame(modal) {
    const frame = openingFrames.get(modal);
    if (frame === undefined) return;

    window.cancelAnimationFrame(frame);
    openingFrames.delete(modal);
  }

  function clearTransitionWait(modal) {
    const cleanup = transitionCleanups.get(modal);
    if (cleanup) cleanup();
  }

  function afterDialogTransition(modal, callback) {
    clearTransitionWait(modal);

    const dialog = getDialog(modal);
    if (!dialog || !shouldAnimateModal()) {
      callback();
      return;
    }

    let finished = false;
    let timer = null;

    const cleanup = () => {
      dialog.removeEventListener('transitionend', handleTransitionEnd);
      if (timer !== null) window.clearTimeout(timer);
      transitionCleanups.delete(modal);
    };

    const finish = () => {
      if (finished) return;
      finished = true;
      cleanup();
      callback();
    };

    const handleTransitionEnd = (event) => {
      if (event.target !== dialog || event.propertyName !== 'transform') return;
      finish();
    };

    dialog.addEventListener('transitionend', handleTransitionEnd);
    timer = window.setTimeout(finish, modalTransitionFallback);
    transitionCleanups.set(modal, cleanup);
  }

  function getFocusableElements(container) {
    if (!container?.querySelectorAll) return [];

    return Array.from(container.querySelectorAll(focusableSelector))
      .filter((element) => element.offsetParent !== null);
  }

  function usesCommaDecimal() {
    return (document.documentElement.lang || '').toLowerCase().startsWith('fi');
  }

  function formatInchNumber(centimeters) {
    let inches = (centimeters / 2.54).toFixed(1);
    inches = inches.replace(/\.0$/, '');

    if (usesCommaDecimal()) {
      inches = inches.replace('.', ',');
    }

    return inches;
  }

  function formatInches(values, separators = []) {
    const converted = values.map((value) => formatInchNumber(value));
    let result = converted[0] || '';

    for (let index = 1; index < converted.length; index += 1) {
      const separator = separators[index - 1] || '–';
      result += ` ${separator} ${converted[index]}`;
    }

    return `${result} in`;
  }

  function stripLegacyMeasurementText(container) {
    const ownerDocument = container?.ownerDocument || document;
    const nodeFilter = ownerDocument.defaultView?.NodeFilter || window.NodeFilter;
    if (!container || !ownerDocument.createTreeWalker || !nodeFilter) return;

    const legacyPatterns = [
      /If you need more information about the product, feel free to get in touch with us!?/gi,
      /^\s*Image\s*$/gi
    ];
    const walker = ownerDocument.createTreeWalker(container, nodeFilter.SHOW_TEXT);
    const textNodes = [];

    while (walker.nextNode()) {
      textNodes.push(walker.currentNode);
    }

    textNodes.forEach((node) => {
      let value = node.nodeValue;
      legacyPatterns.forEach((pattern) => {
        value = value.replace(pattern, '');
      });
      node.nodeValue = value;
    });
  }

  function removeEmptyMeasurementNodes(container) {
    if (!container?.querySelectorAll) return;

    container
      .querySelectorAll('p, li, figure, div')
      .forEach((element) => {
        if (element.textContent.trim() || element.querySelector('img, svg, [data-dn-cm-value], [data-size-cm-value]')) return;
        element.remove();
      });
  }

  const measurementLabels = [
    'shoulder strap drop',
    'shoulder strap length',
    'inside leg length',
    'inseam length',
    'sleeve measurement',
    'chest circumference',
    'waist circumference',
    'shoulder width',
    'body length',
    'total length',
    'front rise',
    'back rise',
    'strap length',
    'strap drop',
    'olkahihnan pituus',
    'olkahihnan pudotus',
    'lahkeen sisäpituus',
    'sisälahkeen pituus',
    'sisäpohjan pituus',
    'pohjallisen pituus',
    'hihapituus',
    'rinnanympärys',
    'rinnan ympärys',
    'vyötärönympärys',
    'vyötärön ympärys',
    'hartioiden leveys',
    'olkapäiden leveys',
    'kokonaispituus',
    'selän pituus',
    'etunousu',
    'takanousu',
    'chest width',
    'bust width',
    'sleeve length',
    'rinnan leveys',
    'vyötärön leveys',
    'hihan pituus',
    'hihnan pituus',
    'circumference',
    'ympärysmitta',
    'leg opening',
    'insole length',
    'inside leg',
    'outseam',
    'halkaisija',
    'diameter',
    'shoulders',
    'shoulder',
    'lahkeen suu',
    'lahkeensuu',
    'sisäsauma',
    'vyötärö',
    'korkeus',
    'leveys',
    'olkapää',
    'hartiat',
    'olkapäät',
    'syvyys',
    'inseam',
    'sleeve',
    'length',
    'chest',
    'bust',
    'waist',
    'height',
    'width',
    'depth',
    'nousu',
    'pituus',
    'rinta',
    'hiha',
    'rise'
  ];
  const escapedMeasurementLabels = [...measurementLabels]
    .sort((a, b) => b.length - a.length)
    .map((label) => label.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const measurementLabelPattern = new RegExp(
    `^[\\s\\-*–—•]*(${escapedMeasurementLabels.join('|')})\\s*(?::|\\/)?[\\s\\u00a0]*(.*)$`,
    'i'
  );
  const measurementPairPattern = new RegExp(
    `(${escapedMeasurementLabels.join('|')})\\s*(?::|\\/)?\\s*(.*?)(?=[\\s\\-*–—•]*(?:${escapedMeasurementLabels.join('|')})\\s*(?::|\\/)?\\s*\\d|$)`,
    'gi'
  );
  const dimensionTripletPattern = /^[\s\-*–—•]*(?:product\s+)?(?:dimensions?|mitat|tuotteen\s+mitat)\s*:?\s*(\d+(?:[.,]\d+)?)\s*[x×]\s*(\d+(?:[.,]\d+)?)\s*[x×]\s*(\d+(?:[.,]\d+)?)\s*cm\b/i;
  const alphaSizeTokenPattern = '(?:XXXL|XXL|XXS|XL|XS|OS|S|M|L|PM|MM|GM)';
  const wordSizeTokenPattern = '(?:EXTRA\\s+EXTRA\\s+EXTRA\\s+LARGE|EXTRA\\s+EXTRA\\s+(?:LARGE|SMALL)|EXTRA\\s+(?:LARGE|SMALL)|X{1,3}[\\s-]?LARGE|X{1,2}[\\s-]?SMALL|SMALL|MEDIUM|LARGE)';
  const numericSizeTokenPattern = '\\d+(?:[.,]\\d+)?(?:\\s*(?:EU|IT|UK|US|CM))?';
  const basicSizeTokenPattern = `(?:ONE[\\s-]?SIZE|${wordSizeTokenPattern}|${alphaSizeTokenPattern}|${numericSizeTokenPattern})`;
  const sizeTokenPattern = `(${basicSizeTokenPattern}(?:\\s*\\/\\s*${basicSizeTokenPattern})?)`;
  const explicitSizePattern = new RegExp(`^[\\s\\-*–—•]*(?:size|koko)\\s*:?\\s*${sizeTokenPattern}\\b`, 'i');
  const standaloneSizePattern = new RegExp(`^[\\s\\-*–—•]*${sizeTokenPattern}[\\s\\-*–—•]*$`, 'i');
  const fitPattern = new RegExp(
    `\\b(?:fits?|fittaa(?:\\s+kokoa)?|vastaa(?:\\s+kokoa)?)\\s*:?\\s*${sizeTokenPattern}`,
    'i'
  );
  const mensFitPattern = new RegExp(
    `\\b(?:fits?|fittaa|vastaa)\\s*:?\\s*(?:men(?:'s|s)?(?:\\s+size)?|miesten(?:\\s+kokoa)?)\\s*${sizeTokenPattern}`,
    'i'
  );
  const shorthandDimensionKeys = {
    L: 'length',
    W: 'width',
    H: 'height',
    D: 'depth'
  };

  function normalizeMeasurementLine(value) {
    return (value || '')
      .replace(/\u00a0/g, ' ')
      .replace(/[ \t]+/g, ' ')
      .replace(/\s+([,.:;])/g, '$1')
      .trim();
  }

  function normalizeMeasurementValue(value) {
    return normalizeMeasurementLine(value)
      .replace(/(\s*cm)\s*,\s*$/i, '$1')
      .trim();
  }

  function shorthandDimensionPairs(line) {
    const normalizedLine = normalizeMeasurementLine(line).replace(/^[\s\-*–—•]+/, '');
    const hasSizeWrapper = /^(?:size|koko)\s*:?\s*/i.test(normalizedLine);
    const source = normalizedLine
      .replace(/^[\s\-*–—•]+/, '')
      .replace(/^(?:size|koko)\s*:?\s*/i, '');
    const pattern = /(?:^|[\s,;|/×x-]+)([LWHD])\s*(:?)\s*(\d+(?:[.,]\d+)?)(?:\s*cm\b)?/gi;
    const matches = [];
    let match;

    while ((match = pattern.exec(source))) {
      matches.push({
        key: shorthandDimensionKeys[match[1].toUpperCase()],
        rawLabel: match[1].toUpperCase(),
        value: `${match[3]} cm`,
        hasColon: match[2] === ':',
        hasOwnUnit: /cm\b/i.test(match[0])
      });
    }

    const hasCentimeters = /cm\b/i.test(source);
    const isDimensionSyntax = matches.length > 1 ||
      (hasSizeWrapper && matches.some((item) => item.hasOwnUnit)) ||
      matches.some((item) => item.hasColon && item.hasOwnUnit);
    if (!hasCentimeters || !isDimensionSyntax) return [];

    return matches.map(({ key, rawLabel, value }) => ({ key, rawLabel, value }));
  }

  function isDimensionalExpression(line) {
    return Boolean(line.match(dimensionTripletPattern) || shorthandDimensionPairs(line).length);
  }

  function getMeasurementLines(container) {
    const lines = [];
    let buffer = '';
    const blockTags = new Set([
      'ADDRESS',
      'ARTICLE',
      'BLOCKQUOTE',
      'DIV',
      'FIGCAPTION',
      'H1',
      'H2',
      'H3',
      'H4',
      'H5',
      'H6',
      'LI',
      'P',
      'SECTION'
    ]);

    function flush() {
      const line = normalizeMeasurementLine(buffer);
      if (line) lines.push(line);
      buffer = '';
    }

    function walk(node) {
      if (node.nodeType === 3) {
        buffer += node.nodeValue || '';
        return;
      }

      if (node.nodeType !== 1) return;

      const tagName = node.tagName;
      if (tagName === 'IMG' || tagName === 'SVG' || tagName === 'PICTURE') return;
      if (tagName === 'BR') {
        flush();
        return;
      }

      const isBlock = blockTags.has(tagName);
      if (isBlock) flush();
      node.childNodes.forEach(walk);
      if (isBlock) flush();
    }

    container.childNodes.forEach(walk);
    flush();

    return lines;
  }

  function normalizeSize(value) {
    let size = normalizeMeasurementLine(value)
      .replace(/^(?:size|koko)\s*:?\s*/i, '')
      .split(/\b(?:fits?|fittaa|vastaa)\b/i)[0]
      .replace(/^[,./|·:;–—-]+|[,./|·:;–—-]+$/g, '')
      .trim();

    if (!size || /^default\s+title$/i.test(size)) return '';
    if (/^one[\s-]?size$/i.test(size)) return 'OS';
    if (/^extra extra extra large$/i.test(size)) return 'XXXL';
    if (/^extra extra large$/i.test(size)) return 'XXL';
    if (/^extra large$/i.test(size)) return 'XL';
    if (/^extra extra small$/i.test(size)) return 'XXS';
    if (/^extra small$/i.test(size)) return 'XS';
    if (/^xxx[\s-]?large$/i.test(size)) return 'XXXL';
    if (/^xx[\s-]?large$/i.test(size)) return 'XXL';
    if (/^x[\s-]?large$/i.test(size)) return 'XL';
    if (/^xx[\s-]?small$/i.test(size)) return 'XXS';
    if (/^x[\s-]?small$/i.test(size)) return 'XS';
    if (/^small$/i.test(size)) return 'S';
    if (/^medium$/i.test(size)) return 'M';
    if (/^large$/i.test(size)) return 'L';

    const token = size.match(new RegExp(`^${sizeTokenPattern}(?=$|[\\s,;(])`, 'i'));
    return (token?.[1] || size)
      .toUpperCase()
      .replace(/\s*\/\s*/g, ' / ')
      .replace(/\s*CM\b/g, ' cm');
  }

  function parseSizeHeading(line, knownSizes) {
    if (isDimensionalExpression(line)) return null;

    const explicitMatch = line.match(explicitSizePattern);
    if (explicitMatch) {
      return {
        size: normalizeSize(explicitMatch[1]),
        fit: normalizeSize(line.match(fitPattern)?.[1] || '')
      };
    }

    const standaloneMatch = line.match(standaloneSizePattern);
    if (!standaloneMatch) return null;

    const size = normalizeSize(standaloneMatch[1]);
    return knownSizes.has(size) ? { size, fit: '' } : null;
  }

  function parseMeasurementGroups(container) {
    const knownSizes = new Set(
      (container.dataset.dnSizeOptions || '')
        .split('||')
        .map(normalizeSize)
        .filter(Boolean)
    );
    const groups = [];
    const leadingLines = [];
    let currentGroup = null;

    getMeasurementLines(container).forEach((line) => {
      const heading = parseSizeHeading(line, knownSizes);
      if (heading) {
        currentGroup = {
          size: heading.size,
          fit: heading.fit,
          lines: []
        };
        groups.push(currentGroup);
        return;
      }

      if (!currentGroup) {
        leadingLines.push(line);
        return;
      }

      const fitMatch = line.match(fitPattern);
      const isFitOnly = fitMatch && line.replace(fitMatch[0], '').replace(/[,./|·:;–—-]/g, '').trim() === '';
      if (!currentGroup.fit && isFitOnly) {
        currentGroup.fit = normalizeSize(fitMatch[1]);
        return;
      }

      currentGroup.lines.push(line);
    });

    const validGroups = groups.filter((group) => {
      const groupText = group.lines.join(' ');
      return /\d+(?:[.,]\d+)?\s*cm\b/i.test(groupText) &&
        measurementLabels.some((label) => groupText.toLowerCase().includes(label));
    });

    if (validGroups.length !== groups.length) return null;
    return { groups: validGroups, leadingLines };
  }

  function replaceSizePlaceholder(template, size) {
    return (template || '__SIZE__').replace('__SIZE__', size);
  }

  function appendMeasurementCopy(ownerDocument, parent, text, className = 'dn-size-modal__measurement-copy') {
    const copy = ownerDocument.createElement('p');
    copy.className = className;
    copy.textContent = text;
    parent.appendChild(copy);
  }

  function isFinnishLocale() {
    return (document.documentElement.lang || '').toLowerCase().startsWith('fi');
  }

  function getMatchingSetSourceText(modal) {
    const source = getPreferredMeasurementSourceTemplate(modal);
    return normalizeMeasurementLine(source?.content?.textContent || source?.textContent || '');
  }

  function parseMatchingSetSizes(modal) {
    const sourceText = getMatchingSetSourceText(modal);
    if (!sourceText) return null;

    const garmentSizePattern = new RegExp(
      `\\b(top|jacket|hoodie|sweatshirt|upper|yläosa|bottoms?|pants|trousers|joggers|shorts|alaosa|housut)\\s*(?:size|koko)?\\s*:?\\s*${sizeTokenPattern}\\b`,
      'gi'
    );
    const matches = [];
    let match;

    while ((match = garmentSizePattern.exec(sourceText))) {
      const label = (match[1] || '').toLowerCase();
      const size = normalizeSize(match[2] || '');
      if (!size) continue;

      let type = '';
      if (/^(?:top|jacket|hoodie|sweatshirt|upper|yläosa)$/.test(label)) type = 'top';
      if (/^(?:bottom|bottoms|pants|trousers|joggers|shorts|alaosa|housut)$/.test(label)) type = 'bottom';
      if (type) matches.push({ type, size, index: match.index });
    }

    const top = matches.find((item) => item.type === 'top')?.size || '';
    const bottom = matches.find((item) => item.type === 'bottom')?.size || '';
    if (!top || !bottom) return null;

    const fitRegex = new RegExp(
      `\\b(?:tracksuit|matching\\s+set|two[\\s-]*piece|set)?\\s*(?:fits?|fittaa(?:\\s+kokoa)?|vastaa(?:\\s+kokoa)?)\\s*:?\\s*${sizeTokenPattern}\\b`,
      'i'
    );
    const fit = normalizeSize(sourceText.match(fitRegex)?.[1] || '');

    return { top, bottom, fit };
  }

  function matchingSetSizeText(set) {
    return isFinnishLocale()
      ? `Yläosa ${set.top} · Alaosa ${set.bottom}`
      : `Top ${set.top} · Bottom ${set.bottom}`;
  }

  function matchingSetFitText(set) {
    if (!set?.fit) return '';
    return isFinnishLocale() ? `Fittaa ${set.fit}` : `Fits ${set.fit}`;
  }

  function matchingSetSubtitleText(set) {
    const fitText = matchingSetFitText(set);
    return fitText ? `${matchingSetSizeText(set)} — ${fitText}` : matchingSetSizeText(set);
  }

  function getWrapperForModal(modal) {
    const wrapper = modal?.closest?.(selectors.wrapper);
    if (wrapper) return wrapper;

    const modalId = modal?.id || '';
    if (!modalId) return null;

    return Array.from(document.querySelectorAll(selectors.open))
      .find((trigger) => getModalTargetId(trigger) === modalId)
      ?.closest?.(selectors.wrapper) || null;
  }

  function applyMatchingSetDisplay(modal) {
    const set = parseMatchingSetSizes(modal);
    if (!set) return null;

    const measurements = modal?.querySelector?.(selectors.measurements);
    if (measurements) {
      measurements.dataset.dnMatchingSet = 'true';
      measurements.dataset.dnSetTopSize = set.top;
      measurements.dataset.dnSetBottomSize = set.bottom;
      measurements.dataset.dnSetFit = set.fit || '';
      measurements.dataset.dnSizeDisplay = matchingSetSubtitleText(set);
    }

    const modalSummary = modal?.querySelector?.('.dn-size-modal__summary');
    if (modalSummary) modalSummary.textContent = matchingSetSubtitleText(set);

    const triggerSummary = getWrapperForModal(modal)?.querySelector?.('.dn-size-measurements__trigger-summary');
    if (triggerSummary) {
      const ownerDocument = triggerSummary.ownerDocument || document;
      triggerSummary.replaceChildren();

      const sizeElement = ownerDocument.createElement('span');
      sizeElement.className = 'dn-fit-summary__tagged';
      sizeElement.textContent = matchingSetSizeText(set);
      triggerSummary.appendChild(sizeElement);

      const fitText = matchingSetFitText(set);
      if (fitText) {
        const fitElement = ownerDocument.createElement('span');
        fitElement.className = 'dn-fit-summary__badge';
        fitElement.textContent = fitText;
        triggerSummary.appendChild(fitElement);
      }
    }

    return set;
  }

  function getMooseKnucklesFitRow(modal) {
    const wrapper = getWrapperForModal(modal);
    const productInfo = wrapper?.closest?.('product-info[data-main-product]');
    return productInfo?.querySelector?.('[data-dn-moose-knuckles-fit-row]') || null;
  }

  function sourceSizeAndFit(template, allowMensFit = false) {
    if (!template?.content) return null;
    const source = (template.ownerDocument || document).createElement('div');
    source.appendChild(template.content.cloneNode(true));
    const lines = getMeasurementLines(source);

    for (let index = 0; index < lines.length; index += 1) {
      const heading = parseSizeHeading(lines[index], new Set());
      if (!heading?.size) continue;

      if (!heading.fit) {
        const firstFitIndex = allowMensFit ? 0 : index + 1;
        const fitSearchLimit = allowMensFit ? lines.length : Math.min(lines.length, index + 4);
        for (let nextIndex = firstFitIndex; nextIndex < fitSearchLimit; nextIndex += 1) {
          const fit = (allowMensFit ? lines[nextIndex].match(mensFitPattern)?.[1] : '') ||
            lines[nextIndex].match(fitPattern)?.[1] || '';
          if (fit) {
            heading.fit = normalizeSize(fit);
            break;
          }
        }
      }
      return heading;
    }

    return null;
  }

  function applyMooseKnucklesFitDisplay(modal) {
    const information = canonicalSourceSizeAndFit(modal);
    const fit = normalizeSize(information?.fit || '');
    if (!fit) return false;

    const row = getMooseKnucklesFitRow(modal);
    if (!row) return false;

    const value = row.querySelector('[data-dn-moose-knuckles-fit-value]');
    const template = row.dataset.dnMooseKnucklesFitTemplate || '__SIZE__';
    if (value) value.textContent = replaceSizePlaceholder(template, fit);
    row.dataset.dnMooseKnucklesFitSize = fit;
    row.hidden = false;
    return true;
  }

  function canonicalSourceSizeAndFit(modal) {
    const measurements = modal?.querySelector?.(selectors.measurements);
    const resolvedSize = normalizeSize(measurements?.dataset?.dnLabelledSize || '');
    const selectedSize = normalizeSize(measurements?.dataset?.dnSelectedSize || '');
    const sharedSize = normalizeSize(measurements?.dataset?.dnSharedLabelledSize || '');
    const sharedFit = normalizeSize(measurements?.dataset?.dnSharedFit || '');
    const allowMensFit = Boolean(getMooseKnucklesFitRow(modal));
    const sourceInformation = [
      modal?.querySelector?.(selectors.sharedMeasurementSource),
      modal?.querySelector?.(selectors.defaultMeasurementSource)
    ]
      .map((template) => sourceSizeAndFit(template, allowMensFit))
      .find((information) => information?.size) || null;

    const size = resolvedSize || selectedSize || sharedSize || sourceInformation?.size || '';
    const fit = sharedFit || measurements?.dataset?.dnSelectedFit || sourceInformation?.fit || '';
    return size ? { size, fit } : null;
  }

  function applyCanonicalSizeDisplay(modal) {
    const measurements = modal?.querySelector?.(selectors.measurements);
    if (!measurements || measurements.dataset.dnMatchingSet === 'true') return false;

    const information = canonicalSourceSizeAndFit(modal);
    if (!information?.size) return false;

    const size = normalizeSize(information.size);
    const fit = normalizeSize(information.fit || measurements.dataset.dnSelectedFit || '');
    const sizeText = replaceSizePlaceholder(measurements.dataset.dnLabelSizeTemplate || '', size);
    const fitText = fit
      ? replaceSizePlaceholder(measurements.dataset.dnLabelFitTemplate || '', fit)
      : '';
    const isTrueToSize = fit && normalizeSize(fit) === normalizeSize(size);
    const summaryText = isTrueToSize
      ? replaceSizePlaceholder(measurements.dataset.dnLabelTrueToSizeTemplate || sizeText, size)
      : fitText
        ? `${sizeText} · ${fitText}`
        : sizeText;

    measurements.dataset.dnLabelledSize = size;
    measurements.dataset.dnSelectedFit = fit;
    measurements.dataset.dnSizeDisplay = summaryText;

    let modalSummary = modal.querySelector('.dn-size-modal__summary');
    if (!modalSummary) {
      const title = modal.querySelector('.dn-size-modal__title');
      if (title) {
        modalSummary = (modal.ownerDocument || document).createElement('p');
        modalSummary.className = 'dn-size-modal__summary';
        title.insertAdjacentElement('afterend', modalSummary);
      }
    }
    if (modalSummary) modalSummary.textContent = summaryText;

    const triggerSummary = getWrapperForModal(modal)?.querySelector?.('.dn-size-measurements__trigger-summary');
    if (triggerSummary) {
      const ownerDocument = triggerSummary.ownerDocument || document;
      triggerSummary.replaceChildren();

      const sizeElement = ownerDocument.createElement('span');
      sizeElement.className = 'dn-fit-summary__tagged';
      sizeElement.textContent = sizeText;
      triggerSummary.appendChild(sizeElement);

      if (fitText && !isTrueToSize) {
        const fitElement = ownerDocument.createElement('span');
        fitElement.className = 'dn-fit-summary__badge';
        fitElement.textContent = fitText;
        triggerSummary.appendChild(fitElement);
      }
    }

    return true;
  }

  function requestDefaultProductSource(handle) {
    if (!handle || typeof window.fetch !== 'function') return Promise.resolve('');
    if (defaultProductSourceRequests.has(handle)) return defaultProductSourceRequests.get(handle);

    const url = `${window.location.origin}/products/${encodeURIComponent(handle)}.js`;
    const request = window.fetch(url, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    })
      .then((response) => response.ok ? response.json() : null)
      .then((product) => typeof product?.description === 'string' ? product.description : '')
      .catch(() => '');

    defaultProductSourceRequests.set(handle, request);
    return request;
  }

  function resetMeasurementPreparation(modal) {
    const measurements = modal?.querySelector?.(selectors.measurements);
    if (!measurements) return;
    const currentUnit = modal.dataset.sizeCurrentUnit || modal.dataset.dnSizeCurrentUnit || 'cm';

    [
      'dnStructuredReady',
      'dnUnitsReady',
      'sizeUnitsReady',
      'dnCardsReady',
      'dnGeneralReady'
    ].forEach((key) => delete measurements.dataset[key]);

    prepareStructuredMeasurements(modal);
    wrapCentimeterValues(modal);
    setMeasurementUnit(modal, currentUnit);
  }

  function hydrateDefaultProductSource(modal) {
    if (!modal || modal.dataset.dnDefaultSourceLoading || modal.dataset.dnDefaultSourceReady) return;
    const handle = modal.dataset.dnProductHandle || '';
    if (!handle) return;

    modal.dataset.dnDefaultSourceLoading = 'true';
    requestDefaultProductSource(handle).then((description) => {
      delete modal.dataset.dnDefaultSourceLoading;
      modal.dataset.dnDefaultSourceReady = 'true';
      if (!description || !modal.isConnected) return;

      let template = modal.querySelector(selectors.defaultMeasurementSource);
      if (!template) {
        template = (modal.ownerDocument || document).createElement('template');
        template.setAttribute('data-dn-default-measurement-source', '');
        modal.appendChild(template);
      }
      template.innerHTML = description;

      applyMatchingSetDisplay(modal);
      applyCanonicalSizeDisplay(modal);
      applyMooseKnucklesFitDisplay(modal);
      resetMeasurementPreparation(modal);
    });
  }

  function appendMeasurementValues(ownerDocument, parent, lines) {
    const values = ownerDocument.createElement('div');
    values.className = 'dn-size-modal__measurement-values';
    let hasValues = false;

    for (let index = 0; index < lines.length; index += 1) {
      const line = lines[index];
      const match = line.match(measurementLabelPattern);
      if (!match) {
        appendMeasurementCopy(ownerDocument, parent, line);
        continue;
      }

      let value = normalizeMeasurementValue(match[2]);
      if (!value && lines[index + 1] && /\d+(?:[.,]\d+)?\s*cm\b/i.test(lines[index + 1])) {
        value = normalizeMeasurementValue(lines[index + 1]);
        index += 1;
      }

      if (!value) {
        appendMeasurementCopy(ownerDocument, parent, line);
        continue;
      }

      const item = ownerDocument.createElement('div');
      item.className = 'dn-size-modal__measurement-value';

      const label = ownerDocument.createElement('strong');
      label.className = 'dn-size-modal__measurement-label';
      label.textContent = match[1];

      const measurement = ownerDocument.createElement('span');
      measurement.className = 'dn-size-modal__measurement-number';
      measurement.textContent = value;

      item.append(label, measurement);
      values.appendChild(item);
      hasValues = true;
    }

    if (hasValues) parent.appendChild(values);
  }

  function createMeasurementCard(container, group, isCurrent) {
    const ownerDocument = container.ownerDocument || document;
    const card = ownerDocument.createElement('article');
    card.className = `dn-size-modal__measurement-card${isCurrent ? ' is-current' : ''}`;

    if (isCurrent) {
      appendMeasurementCopy(
        ownerDocument,
        card,
        container.dataset.dnLabelCurrentSelection || '',
        'dn-size-modal__current-label'
      );
    }

    const header = ownerDocument.createElement('div');
    header.className = 'dn-size-modal__measurement-card-header';

    appendMeasurementCopy(
      ownerDocument,
      header,
      replaceSizePlaceholder(container.dataset.dnLabelSizeTemplate || '', group.size),
      'dn-size-modal__measurement-card-size'
    );

    if (group.fit) {
      appendMeasurementCopy(
        ownerDocument,
        header,
        replaceSizePlaceholder(container.dataset.dnLabelFitTemplate || '', group.fit),
        'dn-size-modal__measurement-card-fit'
      );
    }

    card.appendChild(header);
    appendMeasurementValues(ownerDocument, card, group.lines);
    return card;
  }

  function prepareGeneralMeasurements(modal) {
    const measurements = modal?.querySelector?.(selectors.measurements);
    if (
      !measurements ||
      modal.dataset.dnMeasurementKind !== 'general' ||
      measurements.dataset.dnGeneralReady
    ) {
      return;
    }

    measurements.dataset.dnGeneralReady = 'true';
    const ownerDocument = measurements.ownerDocument || document;
    const values = ownerDocument.createElement('div');
    values.className = 'dn-size-modal__measurement-values dn-size-modal__measurement-values--general';
    const extraLines = [];
    let pairCount = 0;

    function appendValue(labelText, valueText) {
      const item = ownerDocument.createElement('div');
      item.className = 'dn-size-modal__measurement-value';

      const label = ownerDocument.createElement('strong');
      label.className = 'dn-size-modal__measurement-label';
      label.textContent = labelText;

      const measurement = ownerDocument.createElement('span');
      measurement.className = 'dn-size-modal__measurement-number';
      measurement.textContent = valueText;

      item.append(label, measurement);
      values.appendChild(item);
      pairCount += 1;
    }

    getMeasurementLines(measurements).forEach((line) => {
      let foundOnLine = false;
      const dimensionTriplet = line.match(dimensionTripletPattern);

      if (dimensionTriplet) {
        const axisLabels = [
          measurements.dataset.dnLabelWidth || '',
          measurements.dataset.dnLabelHeight || '',
          measurements.dataset.dnLabelDepth || ''
        ];

        dimensionTriplet.slice(1).forEach((value, index) => {
          appendValue(axisLabels[index], `${value} cm`);
        });
        foundOnLine = true;
      }

      if (!foundOnLine) {
        shorthandDimensionPairs(line).forEach((pair) => {
          appendValue(measurementDisplayLabel(measurements, pair), pair.value);
          foundOnLine = true;
        });
      }

      measurementPairPattern.lastIndex = 0;
      let match;

      if (!foundOnLine) {
        while ((match = measurementPairPattern.exec(line))) {
          const value = normalizeMeasurementValue(match[2]);
          if (!/\d+(?:[.,]\d+)?(?:\s*[x×–—-]\s*\d+(?:[.,]\d+)?){0,2}\s*cm\b/i.test(value)) {
            continue;
          }

          appendValue(match[1], value);
          foundOnLine = true;
        }
      }

      if (!foundOnLine) extraLines.push(line);
    });

    if (!pairCount) return;

    const fragment = ownerDocument.createDocumentFragment();
    extraLines.forEach((line) => {
      appendMeasurementCopy(ownerDocument, fragment, line);
    });
    fragment.appendChild(values);
    measurements.replaceChildren(fragment);
  }

  function prepareMeasurementCards(modal) {
    const measurements = modal?.querySelector?.(selectors.measurements);
    if (!measurements || measurements.dataset.dnCardsReady) return;

    measurements.dataset.dnCardsReady = 'true';
    const parsed = parseMeasurementGroups(measurements);
    if (!parsed?.groups.length) return;

    const selectedSize = normalizeSize(measurements.dataset.dnSelectedSize || '');
    const currentIndex = parsed.groups.findIndex((group) => normalizeSize(group.size) === selectedSize);
    const selectedFit = normalizeSize(measurements.dataset.dnSelectedFit || '');

    if (currentIndex !== -1 && selectedFit && !parsed.groups[currentIndex].fit) {
      parsed.groups[currentIndex].fit = selectedFit;
    }

    if (parsed.groups.length === 1) {
      if (selectedSize && currentIndex === -1) {
        const badge = (measurements.ownerDocument || document).createElement('p');
        badge.className = 'dn-size-modal__shown-for-badge';
        badge.textContent = replaceSizePlaceholder(
          measurements.dataset.dnLabelShownForTemplate || '',
          parsed.groups[0].size
        );
        measurements.prepend(badge);
      }
      return;
    }

    const orderedGroups = [...parsed.groups];
    if (currentIndex > 0) {
      orderedGroups.unshift(orderedGroups.splice(currentIndex, 1)[0]);
    }

    const ownerDocument = measurements.ownerDocument || document;
    const fragment = ownerDocument.createDocumentFragment();

    parsed.leadingLines.forEach((line) => {
      appendMeasurementCopy(ownerDocument, fragment, line);
    });

    if (selectedSize && currentIndex === -1) {
      const badge = ownerDocument.createElement('p');
      badge.className = 'dn-size-modal__shown-for-badge';
      badge.textContent = replaceSizePlaceholder(
        measurements.dataset.dnLabelShownForTemplate || '',
        orderedGroups[0].size
      );
      fragment.appendChild(badge);
    }

    const cards = ownerDocument.createElement('div');
    cards.className = 'dn-size-modal__measurement-cards';

    orderedGroups.forEach((group, index) => {
      const isCurrent = currentIndex !== -1 && index === 0;
      if (index === 1) {
        appendMeasurementCopy(
          ownerDocument,
          cards,
          measurements.dataset.dnLabelOtherMeasurements || '',
          'dn-size-modal__other-measurements'
        );
      }
      cards.appendChild(createMeasurementCard(measurements, group, isCurrent));
    });

    fragment.appendChild(cards);
    measurements.replaceChildren(fragment);
    measurements.classList.add('dn-size-modal__measurements--grouped');
  }

  function moveMeasurementIllustration(modal) {
    const measurements = modal?.querySelector?.(selectors.measurements);
    const illustration = modal?.querySelector?.(selectors.illustration);
    const howLayout = modal?.querySelector?.(selectors.howLayout);
    if (!measurements || !illustration || illustration.dataset.dnIllustrationReady) return;

    let measurementMedia = measurements.querySelector('picture, img') || measurements.querySelector('svg');
    if (measurementMedia?.tagName === 'IMG' && measurementMedia.closest('picture')) {
      measurementMedia = measurementMedia.closest('picture');
    }

    if (measurementMedia) illustration.appendChild(measurementMedia);
    measurements.querySelectorAll('figure, picture, img, svg').forEach((element) => element.remove());

    const hasIllustration = Boolean(illustration.children.length);
    illustration.hidden = !hasIllustration;
    illustration.dataset.dnIllustrationReady = 'true';
    howLayout?.classList.toggle('dn-size-modal__how-layout--without-image', !hasIllustration);
    removeEmptyMeasurementNodes(measurements);
  }

  function updateMeasurementExplanations(modal) {
    const measurements = modal?.querySelector?.(selectors.measurements);
    const explanationList = modal?.querySelector?.(selectors.explanations);
    if (!measurements || !explanationList || explanationList.dataset.dnExplanationsReady) return;

    const availableLabels = new Set();
    getMeasurementLines(measurements).forEach((line) => {
      const match = line.match(measurementLabelPattern);
      if (match?.[1]) availableLabels.add(match[1].toLowerCase());
    });

    const relevantLabels = {
      chest: ['chest', 'chest width', 'rinta', 'rinnan leveys'],
      length: ['length', 'pituus'],
      sleeve: ['sleeve', 'sleeve length', 'hiha', 'hihan pituus'],
      waist: ['waist', 'vyötärö'],
      inseam: ['inseam', 'lahkeen sisäpituus'],
      width: ['width', 'leveys']
    };
    let visibleCount = 0;

    explanationList.querySelectorAll(selectors.explanation).forEach((item) => {
      const labels = relevantLabels[item.dataset.dnMeasurementExplanation] || [];
      const isRelevant = labels.some((label) => availableLabels.has(label));
      item.hidden = !isRelevant;
      if (isRelevant) visibleCount += 1;
    });

    explanationList.hidden = visibleCount === 0;
    explanationList.dataset.dnExplanationsReady = 'true';
  }

  function templateHasMeasurementSource(template) {
    if (!template?.content) return false;
    const sourceText = normalizeMeasurementLine(template.content.textContent).toLowerCase();
    const hasMeasurementText = /\d+(?:[.,]\d+)?\s*cm\b/i.test(sourceText) && (
      measurementLabels.some((label) => sourceText.includes(label)) ||
      /(?:dimensions?|mitat|mittatiedot)/i.test(sourceText) ||
      shorthandDimensionPairs(sourceText).length > 0
    );
    return hasMeasurementText;
  }

  function getPreferredMeasurementSourceTemplate(modal) {
    return [
      selectors.sharedMeasurementSource,
      selectors.defaultMeasurementSource,
      selectors.measurementSource
    ]
      .map((selector) => modal?.querySelector?.(selector))
      .find(templateHasMeasurementSource) || null;
  }

  function findMeasurementSectionSource(modal, measurements) {
    const ownerDocument = modal?.ownerDocument || document;
    const source = ownerDocument.createElement('div');
    const template = getPreferredMeasurementSourceTemplate(modal);

    if (template?.content) {
      source.appendChild(template.content.cloneNode(true));
    } else if (measurements) {
      source.innerHTML = measurements.innerHTML;
      return source;
    }

    const markerPattern = /^(?:size info(?:rmation)?|product measurements?|measurements?|tuotteen mitat|tuotteen mittatiedot|mittatiedot)\s*:?(?:\s|$)/i;
    const marker = Array.from(source.querySelectorAll('summary, h1, h2, h3, h4, h5, h6, p, div, strong, b, span'))
      .find((element) => markerPattern.test(normalizeMeasurementLine(element.textContent)));

    if (!marker) {
      const sourceText = getMeasurementLines(source).join(' ').toLowerCase();
      const containsMeasurementValues = /\d+(?:[.,]\d+)?\s*cm\b/i.test(sourceText) &&
        (measurementLabels.some((label) => sourceText.includes(label)) ||
          shorthandDimensionPairs(sourceText).length > 0);
      if (containsMeasurementValues) return source;

      const fallback = ownerDocument.createElement('div');
      fallback.innerHTML = measurements?.innerHTML || '';
      return fallback;
    }

    let sectionStart = marker;
    while (sectionStart.parentElement && sectionStart.parentElement !== source) {
      sectionStart = sectionStart.parentElement;
    }

    const measurementSection = ownerDocument.createElement('div');
    let sibling = sectionStart;
    while (sibling) {
      measurementSection.appendChild(sibling.cloneNode(true));
      sibling = sibling.nextSibling;
    }
    return measurementSection;
  }

  function firstSrcsetUrl(value) {
    return (value || '')
      .split(',')[0]
      ?.trim()
      .split(/\s+/)[0] || '';
  }

  function validEmbeddedMedia(media) {
    if (!media) return null;
    if (media.tagName === 'SVG') return media;

    const picture = media.tagName === 'PICTURE' ? media : media.closest?.('picture');
    const image = picture?.querySelector('img') || (media.tagName === 'IMG' ? media : null);
    if (!image) return null;

    const pictureSource = picture?.querySelector('source');
    const dataSrc = image.getAttribute('data-src') || '';
    const dataSrcset = image.getAttribute('data-srcset') || pictureSource?.getAttribute('data-srcset') || '';
    const srcset = image.getAttribute('srcset') || pictureSource?.getAttribute('srcset') || '';
    const src = image.getAttribute('src') || '';
    const source = [
      dataSrc,
      image.currentSrc,
      firstSrcsetUrl(dataSrcset),
      firstSrcsetUrl(srcset),
      src
    ].find((value) => value && value.trim());

    if (!source) return null;
    if (dataSrc || !src.trim()) {
      image.setAttribute('src', source);
    }
    if (dataSrcset) image.setAttribute('srcset', dataSrcset);
    image.setAttribute('loading', 'lazy');
    image.setAttribute('decoding', 'async');
    return picture || image;
  }

  function getStructuredMeasurementEvents(container) {
    const events = [];
    let buffer = '';
    const blockTags = new Set([
      'ADDRESS', 'ARTICLE', 'BLOCKQUOTE', 'DIV', 'FIGCAPTION', 'H1', 'H2', 'H3', 'H4',
      'H5', 'H6', 'LI', 'P', 'SECTION', 'SUMMARY', 'TD', 'TH', 'TR'
    ]);

    function flush() {
      const line = normalizeMeasurementLine(buffer);
      if (line) events.push({ type: 'line', value: line });
      buffer = '';
    }

    function walk(node) {
      if (node.nodeType === 3) {
        buffer += node.nodeValue || '';
        return;
      }
      if (node.nodeType !== 1) return;

      const tagName = node.tagName;
      if (tagName === 'PICTURE' || tagName === 'IMG' || tagName === 'SVG') {
        flush();
        const media = validEmbeddedMedia(node);
        if (media) events.push({ type: 'media', media });
        return;
      }
      if (tagName === 'BR') {
        flush();
        return;
      }

      const isBlock = blockTags.has(tagName);
      if (isBlock) flush();
      node.childNodes.forEach(walk);
      if (isBlock) flush();
    }

    container.childNodes.forEach(walk);
    flush();
    return events;
  }

  function parseGarmentHeading(value) {
    const heading = normalizeMeasurementLine(value)
      .replace(/^[\s\-*–—•]+|[\s:.-]+$/g, '')
      .toLowerCase();
    const topHeadings = new Set(['top', 'upper', 'upper body', 'yläosa']);
    const bottomHeadings = new Set(['bottom', 'bottoms', 'pants', 'trousers', 'housut', 'alaosa']);

    if (topHeadings.has(heading)) return 'top';
    if (bottomHeadings.has(heading)) return 'bottom';
    return '';
  }

  function canonicalMeasurementKey(label) {
    const normalized = normalizeMeasurementLine(label).toLowerCase();
    const aliases = {
      chest: ['chest', 'chest width', 'chest circumference', 'bust', 'bust width', 'rinta', 'rinnan leveys', 'rinnanympärys', 'rinnan ympärys'],
      length: ['length', 'body length', 'total length', 'outseam', 'pituus', 'kokonaispituus', 'selän pituus'],
      sleeve: ['sleeve', 'sleeve length', 'sleeve measurement', 'hiha', 'hihan pituus', 'hihapituus'],
      shoulders: ['shoulder', 'shoulders', 'shoulder width', 'olkapää', 'hartiat', 'olkapäät', 'hartioiden leveys', 'olkapäiden leveys'],
      waist: ['waist', 'waist circumference', 'vyötärö', 'vyötärön leveys', 'vyötärönympärys', 'vyötärön ympärys'],
      leg_opening: ['leg opening', 'lahkeensuu', 'lahkeen suu'],
      inseam: ['inseam', 'inseam length', 'inside leg', 'inside leg length', 'lahkeen sisäpituus', 'sisälahkeen pituus', 'sisäsauma'],
      rise: ['rise', 'front rise', 'back rise', 'nousu', 'etunousu', 'takanousu'],
      width: ['width', 'leveys'],
      height: ['height', 'korkeus'],
      depth: ['depth', 'syvyys'],
      circumference: ['circumference', 'ympärysmitta'],
      diameter: ['diameter', 'halkaisija'],
      insole_length: ['insole length', 'sisäpohjan pituus', 'pohjallisen pituus'],
      strap_drop: ['strap drop', 'shoulder strap drop', 'olkahihnan pudotus'],
      strap_length: ['strap length', 'shoulder strap length', 'hihnan pituus', 'olkahihnan pituus']
    };

    return Object.keys(aliases).find((key) => aliases[key].includes(normalized)) || normalized.replace(/\s+/g, '_');
  }

  function structuredValue(value) {
    const normalized = normalizeMeasurementValue(value);
    return normalized.match(/\d+(?:[.,]\d+)?(?:\s*[x×–—-]\s*\d+(?:[.,]\d+)?){0,2}\s*cm\b/i)?.[0] || '';
  }

  function measurementPairsFromLine(line) {
    const pairs = [];
    const dimensionTriplet = line.match(dimensionTripletPattern);
    if (dimensionTriplet) {
      ['width', 'height', 'depth'].forEach((key, index) => {
        pairs.push({ key, rawLabel: key, value: `${dimensionTriplet[index + 1]} cm` });
      });
      return pairs;
    }

    const shorthandPairs = shorthandDimensionPairs(line);
    if (shorthandPairs.length) return shorthandPairs;

    measurementPairPattern.lastIndex = 0;
    let match;
    while ((match = measurementPairPattern.exec(line))) {
      const value = structuredValue(match[2]);
      if (!value) continue;
      pairs.push({
        key: canonicalMeasurementKey(match[1]),
        rawLabel: normalizeMeasurementLine(match[1]),
        value
      });
    }
    return pairs;
  }

  function classifyGarmentType(group, modal) {
    if (group.explicitType) return group.explicitType;
    const keys = new Set(group.sizeSets.flatMap((set) => set.pairs.map((pair) => pair.key)));
    const hasBottomSignal = ['waist', 'leg_opening', 'inseam', 'rise'].some((key) => keys.has(key));
    const hasTopSignal = ['chest', 'sleeve', 'shoulders'].some((key) => keys.has(key));

    if (hasBottomSignal && !hasTopSignal) return 'bottom';
    if (hasTopSignal && !hasBottomSignal) return 'top';

    const title = (modal?.dataset?.dnProductTitle || '').toLowerCase();
    if (/\b(?:trousers|pants|jeans|joggers|shorts)\b/.test(title)) return 'bottom';
    if (/\b(?:jacket|hoodie|sweatshirt|shirt|vest|gilet|parka|coat|top)\b/.test(title)) return 'top';
    if (modal?.dataset?.dnMeasurementKind === 'general') return 'general';
    return hasBottomSignal ? 'bottom' : hasTopSignal ? 'top' : 'general';
  }

  function parseStructuredMeasurementGroups(modal, measurements) {
    const source = findMeasurementSectionSource(modal, measurements);
    const events = getStructuredMeasurementEvents(source);
    const knownSizes = new Set(
      (measurements.dataset.dnSizeOptions || '')
        .split('||')
        .map(normalizeSize)
        .filter(Boolean)
    );
    const headings = events
      .map((event, index) => ({ index, type: event.type === 'line' ? parseGarmentHeading(event.value) : '' }))
      .filter((heading) => heading.type);
    const groupRanges = headings.length
      ? headings.map((heading, index) => ({
          explicitType: heading.type,
          headingIndex: heading.index,
          start: index === 0 ? 0 : heading.index,
          end: headings[index + 1] ? headings[index + 1].index - 1 : events.length - 1,
          sourceIndex: index
        }))
      : [{ explicitType: '', headingIndex: -1, start: 0, end: events.length - 1, sourceIndex: 0 }];

    const groups = groupRanges.map((range) => {
      const sizeSets = [];
      const media = [];
      let currentSet = { size: '', fit: '', pairs: [] };
      let hasExplicitSizes = false;

      for (let index = range.start; index <= range.end; index += 1) {
        const event = events[index];
        if (!event) continue;
        if (event.type === 'media') {
          media.push(event.media);
          continue;
        }
        if (index === range.headingIndex || parseGarmentHeading(event.value)) continue;

        const sizeHeading = parseSizeHeading(event.value, knownSizes);
        if (sizeHeading) {
          if (currentSet.pairs.length) sizeSets.push(currentSet);
          currentSet = { size: sizeHeading.size, fit: sizeHeading.fit, pairs: [] };
          hasExplicitSizes = true;
          continue;
        }

        const fitMatch = event.value.match(fitPattern);
        const isFitOnly = fitMatch && event.value.replace(fitMatch[0], '').replace(/[,./|·:;–—-]/g, '').trim() === '';
        if (isFitOnly && !currentSet.fit) {
          currentSet.fit = normalizeSize(fitMatch[1]);
          continue;
        }

        currentSet.pairs.push(...measurementPairsFromLine(event.value));
      }

      if (currentSet.pairs.length) sizeSets.push(currentSet);
      const group = { ...range, sizeSets, media, hasExplicitSizes };
      group.type = classifyGarmentType(group, modal);
      return group;
    }).filter((group) => group.sizeSets.some((set) => set.pairs.length));

    return groups.sort((a, b) => {
      if (groups.length < 2) return a.sourceIndex - b.sourceIndex;
      const order = { top: 0, bottom: 1, general: 2 };
      return (order[a.type] ?? 3) - (order[b.type] ?? 3) || a.sourceIndex - b.sourceIndex;
    });
  }

  function measurementDisplayLabel(measurements, pair) {
    const labels = {
      chest: measurements.dataset.dnMeasurementLabelChest,
      length: measurements.dataset.dnMeasurementLabelLength,
      sleeve: measurements.dataset.dnMeasurementLabelSleeve,
      shoulders: measurements.dataset.dnMeasurementLabelShoulders,
      waist: measurements.dataset.dnMeasurementLabelWaist,
      leg_opening: measurements.dataset.dnMeasurementLabelLegOpening,
      inseam: measurements.dataset.dnMeasurementLabelInseam,
      rise: measurements.dataset.dnMeasurementLabelRise,
      width: measurements.dataset.dnLabelWidth,
      height: measurements.dataset.dnLabelHeight,
      depth: measurements.dataset.dnLabelDepth,
      circumference: measurements.dataset.dnMeasurementLabelCircumference,
      diameter: measurements.dataset.dnMeasurementLabelDiameter,
      insole_length: measurements.dataset.dnMeasurementLabelInsoleLength,
      strap_drop: measurements.dataset.dnMeasurementLabelStrapDrop,
      strap_length: measurements.dataset.dnMeasurementLabelStrapLength
    };
    return labels[pair.key] || pair.rawLabel;
  }

  function appendStructuredMeasurementValues(measurements, parent, pairs) {
    const ownerDocument = measurements.ownerDocument || document;
    const values = ownerDocument.createElement('div');
    values.className = 'dn-size-modal__measurement-values';

    pairs.forEach((pair) => {
      const item = ownerDocument.createElement('div');
      item.className = 'dn-size-modal__measurement-value';
      const label = ownerDocument.createElement('strong');
      label.className = 'dn-size-modal__measurement-label';
      label.textContent = measurementDisplayLabel(measurements, pair);
      const measurement = ownerDocument.createElement('span');
      measurement.className = 'dn-size-modal__measurement-number';
      measurement.textContent = pair.value;
      item.append(label, measurement);
      values.appendChild(item);
    });

    if (values.children.length) parent.appendChild(values);
  }

  function groupDisplayHeading(measurements, group) {
    if (measurements.dataset.dnMatchingSet === 'true') {
      const size = group.type === 'top'
        ? measurements.dataset.dnSetTopSize
        : group.type === 'bottom'
          ? measurements.dataset.dnSetBottomSize
          : '';
      if (size) {
        const label = group.type === 'top'
          ? measurements.dataset.dnGroupTop || ''
          : measurements.dataset.dnGroupPants || '';
        const sizeText = replaceSizePlaceholder(measurements.dataset.dnLabelSizeTemplate || '', size);
        if (label && sizeText) return `${label} — ${sizeText}`.toUpperCase();
      }
    }

    if (group.type === 'top') return measurements.dataset.dnGroupTop || '';
    if (group.type === 'bottom') return measurements.dataset.dnGroupPants || '';
    return '';
  }

  function selectedSizeForMeasurementGroup(measurements, group) {
    if (measurements.dataset.dnMatchingSet === 'true') {
      if (group.type === 'top') return normalizeSize(measurements.dataset.dnSetTopSize || '');
      if (group.type === 'bottom') return normalizeSize(measurements.dataset.dnSetBottomSize || '');
    }

    return normalizeSize(measurements.dataset.dnLabelledSize || measurements.dataset.dnSelectedSize || '');
  }

  function sizeLabelForMeasurementGroup(measurements, group, size) {
    if (measurements.dataset.dnMatchingSet === 'true') {
      if (group.type === 'top') {
        return replaceSizePlaceholder(measurements.dataset.dnLabelSetTopSizeTemplate || '', size);
      }
      if (group.type === 'bottom') {
        return replaceSizePlaceholder(measurements.dataset.dnLabelSetBottomSizeTemplate || '', size);
      }
    }

    return replaceSizePlaceholder(measurements.dataset.dnLabelSizeTemplate || '', size);
  }

  function renderStructuredMeasurementGroups(measurements, groups) {
    const ownerDocument = measurements.ownerDocument || document;
    const fragment = ownerDocument.createDocumentFragment();
    const isMatchingSet = measurements.dataset.dnMatchingSet === 'true';
    const selectedFit = normalizeSize(measurements.dataset.dnSetFit || measurements.dataset.dnSelectedFit || '');
    const showGroupHeadings = groups.length > 1;

    groups.forEach((group) => {
      const section = ownerDocument.createElement('section');
      section.className = 'dn-size-modal__measurement-group';
      const headingText = groupDisplayHeading(measurements, group);
      if (showGroupHeadings && headingText) {
        const heading = ownerDocument.createElement('h4');
        heading.className = 'dn-size-modal__measurement-group-heading';
        heading.textContent = headingText;
        section.appendChild(heading);
      }

      if (group.hasExplicitSizes || group.sizeSets.length > 1) {
        const cards = ownerDocument.createElement('div');
        cards.className = 'dn-size-modal__measurement-cards';
        const orderedSets = [...group.sizeSets];
        const selectedSize = selectedSizeForMeasurementGroup(measurements, group);
        const currentIndex = orderedSets.findIndex((set) => normalizeSize(set.size) === selectedSize);
        if (currentIndex > 0) orderedSets.unshift(orderedSets.splice(currentIndex, 1)[0]);

        orderedSets.forEach((set, index) => {
          const card = ownerDocument.createElement('article');
          const isCurrent = currentIndex !== -1 && index === 0;
          card.className = `dn-size-modal__measurement-card${isCurrent ? ' is-current' : ''}`;
          if (isCurrent) {
            appendMeasurementCopy(ownerDocument, card, measurements.dataset.dnLabelCurrentSelection || '', 'dn-size-modal__current-label');
          }
          const header = ownerDocument.createElement('div');
          header.className = 'dn-size-modal__measurement-card-header';
          appendMeasurementCopy(ownerDocument, header, sizeLabelForMeasurementGroup(measurements, group, set.size), 'dn-size-modal__measurement-card-size');
          const fit = set.fit || (isCurrent && !isMatchingSet ? selectedFit : '');
          if (fit) {
            appendMeasurementCopy(ownerDocument, header, replaceSizePlaceholder(measurements.dataset.dnLabelFitTemplate || '', fit), 'dn-size-modal__measurement-card-fit');
          }
          card.appendChild(header);
          appendStructuredMeasurementValues(measurements, card, set.pairs);
          cards.appendChild(card);
        });
        section.appendChild(cards);
      } else {
        appendStructuredMeasurementValues(measurements, section, group.sizeSets[0]?.pairs || []);
      }
      fragment.appendChild(section);
    });

    measurements.replaceChildren(fragment);
    measurements.classList.toggle('dn-size-modal__measurements--grouped', groups.length > 1);
  }

  function instructionForPair(modal, measurements, groupType, key) {
    const guideType = modal?.dataset?.dnMeasurementGuideType || 'auto';
    const instructionsByType = {
      top: {
        chest: measurements.dataset.dnInstructionTopChest,
        length: measurements.dataset.dnInstructionTopLength,
        sleeve: measurements.dataset.dnInstructionTopSleeve,
        shoulders: measurements.dataset.dnInstructionTopShoulders
      },
      bottom: {
        waist: measurements.dataset.dnInstructionBottomWaist,
        length: measurements.dataset.dnInstructionBottomLength,
        leg_opening: measurements.dataset.dnInstructionBottomLegOpening,
        inseam: measurements.dataset.dnInstructionBottomInseam,
        rise: measurements.dataset.dnInstructionBottomRise
      },
      bag: {
        width: measurements.dataset.dnInstructionBagWidth,
        height: measurements.dataset.dnInstructionBagHeight,
        length: measurements.dataset.dnInstructionBagLength,
        depth: measurements.dataset.dnInstructionBagDepth,
        strap_drop: measurements.dataset.dnInstructionBagStrapDrop,
        strap_length: measurements.dataset.dnInstructionBagStrapLength
      },
      general: {
        width: measurements.dataset.dnInstructionGeneralWidth
      }
    };

    if (guideType === 'bag') return instructionsByType.bag[key] || '';
    if (instructionsByType[guideType]?.[key]) return instructionsByType[guideType][key];
    return instructionsByType[groupType]?.[key] || instructionsByType.general[key] || '';
  }

  function renderMeasurementGuides(modal, measurements, groups) {
    const guides = modal?.querySelector?.(selectors.guides);
    if (!guides) return;
    const ownerDocument = guides.ownerDocument || document;
    const fragment = ownerDocument.createDocumentFragment();
    const showGroupHeadings = groups.length > 1;

    groups.forEach((group) => {
      const uniquePairs = [];
      const seenKeys = new Set();
      group.sizeSets.flatMap((set) => set.pairs).forEach((pair) => {
        if (seenKeys.has(pair.key)) return;
        seenKeys.add(pair.key);
        const instruction = instructionForPair(modal, measurements, group.type, pair.key);
        if (instruction) uniquePairs.push({ ...pair, instruction });
      });
      if (!group.media.length && !uniquePairs.length) return;

      const guide = ownerDocument.createElement('section');
      guide.className = 'dn-size-modal__guide';
      const headingText = groupDisplayHeading(measurements, group);
      if (showGroupHeadings && headingText) {
        const heading = ownerDocument.createElement('h3');
        heading.className = 'dn-size-modal__guide-heading';
        heading.textContent = headingText;
        guide.appendChild(heading);
      }

      const layout = ownerDocument.createElement('div');
      layout.className = 'dn-size-modal__guide-layout';
      const images = ownerDocument.createElement('div');
      images.className = 'dn-size-modal__guide-images';
      group.media.forEach((media) => {
        const frame = ownerDocument.createElement('div');
        frame.className = 'dn-size-modal__measurement-image';
        const image = media.tagName === 'PICTURE' ? media.querySelector('img') : media.tagName === 'IMG' ? media : null;
        if (image && !image.getAttribute('alt')) {
          image.setAttribute('alt', headingText || measurements.dataset.dnMeasurementDiagramAlt || '');
        }
        if (image) {
          image.addEventListener('error', () => {
            frame.remove();
            if (!images.children.length) {
              images.remove();
              layout.classList.add('dn-size-modal__guide-layout--without-image');
            }
          }, { once: true });
        }
        frame.appendChild(media);
        images.appendChild(frame);
      });

      if (images.children.length) {
        layout.appendChild(images);
      } else {
        layout.classList.add('dn-size-modal__guide-layout--without-image');
      }

      if (uniquePairs.length) {
        const explanations = ownerDocument.createElement('ul');
        explanations.className = 'dn-size-modal__explanations';
        uniquePairs.forEach((pair) => {
          const item = ownerDocument.createElement('li');
          const label = ownerDocument.createElement('strong');
          label.textContent = measurementDisplayLabel(measurements, pair);
          const text = ownerDocument.createElement('span');
          text.textContent = pair.instruction;
          item.append(label, text);
          explanations.appendChild(item);
        });
        layout.appendChild(explanations);
      }
      guide.appendChild(layout);
      fragment.appendChild(guide);
    });

    guides.replaceChildren(fragment);
    guides.hidden = !guides.children.length;
  }

  function prepareStructuredMeasurements(modal) {
    const measurements = modal?.querySelector?.(selectors.measurements);
    if (!measurements || measurements.dataset.dnStructuredReady) return;
    const groups = parseStructuredMeasurementGroups(modal, measurements);
    measurements.dataset.dnMeasurementsValid = String(groups.length > 0);
    const units = modal.querySelector('.dn-size-modal__unit-toggle');
    if (!groups.length) {
      const source = findMeasurementSectionSource(modal, measurements);
      const hasUnparsedValues = getMeasurementLines(source).some((line) => {
        return measurementLabels.some((label) => line.toLowerCase().includes(label)) &&
          /\d+(?:[.,]\d+)?\s*(?:cm|inches?|in\b|”|")/i.test(line);
      });
      if (!hasUnparsedValues && measurements.dataset.dnMeasurementsUnavailable) {
        const guidancePairs = getMeasurementLines(source).flatMap((line) => {
          const match = line.match(measurementLabelPattern);
          return match ? [{ key: canonicalMeasurementKey(match[1]), rawLabel: match[1] }] : [];
        });
        if (guidancePairs.length) {
          const guidance = { explicitType: '', sizeSets: [{ pairs: guidancePairs }], media: [] };
          guidance.type = classifyGarmentType(guidance, modal);
          renderMeasurementGuides(modal, measurements, [guidance]);
        }
        const message = (modal.ownerDocument || document).createElement('p');
        message.textContent = measurements.dataset.dnMeasurementsUnavailable;
        measurements.replaceChildren(message);
        if (units) units.hidden = true;
      }
      return;
    }

    if (units) units.hidden = false;
    renderStructuredMeasurementGroups(measurements, groups);
    renderMeasurementGuides(modal, measurements, groups);
    measurements.dataset.dnStructuredReady = 'true';
  }

  function wrapCentimeterValues(modal) {
    const measurements = modal?.querySelector?.(selectors.measurements);
    if (!measurements || measurements.dataset.dnUnitsReady || measurements.dataset.sizeUnitsReady) return;

    const ownerDocument = measurements.ownerDocument || document;
    const nodeFilter = ownerDocument.defaultView?.NodeFilter || window.NodeFilter;
    if (!ownerDocument.createTreeWalker || !nodeFilter) return;

    const textNodes = [];
    const walker = ownerDocument.createTreeWalker(measurements, nodeFilter.SHOW_TEXT);
    const centimeterPattern = /(\d+(?:[.,]\d+)?)(?:\s*([x×–—-])\s*(\d+(?:[.,]\d+)?))?(?:\s*([x×])\s*(\d+(?:[.,]\d+)?))?\s*cm\b(?:\s*,)?/gi;

    while (walker.nextNode()) {
      const node = walker.currentNode;
      if (node.parentElement?.closest('[data-dn-cm-value], [data-size-cm-value]')) continue;
      if (centimeterPattern.test(node.nodeValue)) {
        textNodes.push(node);
      }
      centimeterPattern.lastIndex = 0;
    }

    textNodes.forEach((node) => {
      if (!node.parentNode) return;

      const fragment = ownerDocument.createDocumentFragment();
      const text = node.nodeValue;
      let lastIndex = 0;
      let match;

      centimeterPattern.lastIndex = 0;
      while ((match = centimeterPattern.exec(text))) {
        fragment.append(ownerDocument.createTextNode(text.slice(lastIndex, match.index)));

        const values = [match[1], match[3], match[5]]
          .filter(Boolean)
          .map((value) => Number.parseFloat(value.replace(',', '.')));
        const separators = [match[2], match[4]].filter(Boolean);
        const display = `${match[1]} cm`;
        const originalDisplay = match[0]
          .replace(/\s*,\s*$/, '')
          .replace(/\s*cm$/i, ' cm');
        const valueElement = ownerDocument.createElement('span');
        valueElement.dataset.dnCmValue = values.join('|');
        valueElement.dataset.dnCmSeparators = separators.join('|');
        valueElement.dataset.dnCmText = originalDisplay || display;
        valueElement.dataset.sizeCmValue = values.join('|');
        valueElement.dataset.sizeCmText = originalDisplay || display;
        valueElement.textContent = originalDisplay || display;
        fragment.append(valueElement);

        lastIndex = match.index + match[0].length;
      }

      fragment.append(ownerDocument.createTextNode(text.slice(lastIndex)));
      node.parentNode.replaceChild(fragment, node);
    });

    measurements.dataset.dnUnitsReady = 'true';
    measurements.dataset.sizeUnitsReady = 'true';
    removeEmptyMeasurementNodes(measurements);
  }

  function setMeasurementUnit(modal, unit) {
    if (!modal || !unit) return;

    modal.dataset.dnSizeCurrentUnit = unit;
    modal.dataset.sizeCurrentUnit = unit;
    modal.removeAttribute('data-dn-size-unit');
    modal.removeAttribute('data-size-unit');

    modal.querySelectorAll('[data-dn-cm-value], [data-size-cm-value]').forEach((element) => {
      const centimeters = (element.dataset.sizeCmValue || element.dataset.dnCmValue || '')
        .split('|')
        .map((value) => Number.parseFloat(value))
        .filter((value) => !Number.isNaN(value));
      if (!centimeters.length) return;
      const separators = (element.dataset.dnCmSeparators || '').split('|').filter(Boolean);

      element.textContent = unit === 'in'
        ? formatInches(centimeters, separators)
        : element.dataset.sizeCmText || element.dataset.dnCmText || `${centimeters[0]} cm`;
    });

    const notice = modal.querySelector(selectors.notice);
    if (notice) {
      notice.textContent = unit === 'in'
        ? notice.dataset.dnSizeNoticeIn
        : notice.dataset.dnSizeNoticeCm;
    }

    modal.querySelectorAll(selectors.unit).forEach((button) => {
      const buttonUnit = button.dataset.sizeUnit || button.dataset.dnSizeUnit;
      const isActive = buttonUnit === unit;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });
  }

  function prepareModal(root) {
    const modal = root?.matches?.(selectors.modal)
      ? root
      : root?.querySelector?.(selectors.modal);
    if (!modal) return;

    try {
      applyMatchingSetDisplay(modal);
      applyCanonicalSizeDisplay(modal);
      applyMooseKnucklesFitDisplay(modal);
      const measurements = modal.querySelector(selectors.measurements);
      if (measurements) {
        prepareStructuredMeasurements(modal);
        const hasMeasurements = measurements.dataset.dnMeasurementsValid === 'true';
        const wrapper = getWrapperForModal(modal);
        if (wrapper) {
          wrapper.hidden = !hasMeasurements;
          const slot = wrapper.closest('[data-dn-measurement-slot]');
          if (slot) slot.hidden = !hasMeasurements;
        }
        wrapCentimeterValues(modal);
      }

      setMeasurementUnit(
        modal,
        modal.dataset.sizeCurrentUnit ||
          modal.dataset.dnSizeCurrentUnit ||
          modal.dataset.sizeUnit ||
          modal.dataset.dnSizeUnit ||
          'cm'
      );
      hydrateDefaultProductSource(modal);
    } catch (error) {
      console.warn('DRIPNORD Size & Measurements preparation skipped.', error);
    }
  }

  function prepareAllModals(root = document) {
    getModals(root).forEach(prepareModal);
  }

  function getProductInfoRoots(root = document) {
    const productInfos = [];

    if (root?.matches?.('product-info[data-main-product]')) {
      productInfos.push(root);
    }

    root?.querySelectorAll?.('product-info[data-main-product]').forEach((productInfo) => {
      if (!productInfos.includes(productInfo)) productInfos.push(productInfo);
    });

    return productInfos;
  }

  function placeProductInformationHierarchy(root = document) {
    getProductInfoRoots(root).forEach((productInfo) => {
      const measurements = productInfo.querySelector(selectors.wrapper);
      const measurementSlot = productInfo.querySelector('[data-dn-measurement-slot]');
      if (measurements && measurementSlot && !measurementSlot.contains(measurements)) {
        measurementSlot.appendChild(measurements);
      }
    });
  }

  function findModalForTrigger(trigger) {
    const targetId = getModalTargetId(trigger);
    if (targetId) {
      const modalById = document.getElementById(targetId);
      if (modalById?.matches?.(selectors.modal)) return modalById;
    }

    return trigger?.closest?.(selectors.wrapper)?.querySelector?.(selectors.modal) ||
      document.querySelector(selectors.modal);
  }

  function focusModalDialog(modal, dialog) {
    if (!modal?.isConnected || modal.dataset.dnModalState !== 'open') return;

    const closeButton = dialog.querySelector(selectors.close);
    (closeButton || dialog).focus({ preventScroll: true });
  }

  function openModal(trigger) {
    const modal = findModalForTrigger(trigger);
    if (modal) mountModalInPortal(modal);
    const dialog = getDialog(modal);
    if (!modal || !dialog) return;

    const modalState = modal.dataset.dnModalState || 'closed';
    if (modalState === 'opening' || modalState === 'open' || modalState === 'closing') return;

    const currentlyOpenModal = getOpenModal();
    if (currentlyOpenModal && currentlyOpenModal !== modal) {
      closeModal(false, currentlyOpenModal, true);
    }

    prepareModal(modal);
    if (modal.querySelector(selectors.measurements)?.dataset.dnMeasurementsValid !== 'true') return;
    setActiveModalTab(modal, 'measurements');
    cancelOpeningFrame(modal);
    clearTransitionWait(modal);

    activeModal = modal;
    activeDialog = dialog;
    activeTrigger = trigger;
    modal.dataset.dnModalState = 'opening';
    modal.classList.remove('is-open');
    modal.classList.add('is-transitioning');
    modal.hidden = false;
    modal.removeAttribute('aria-hidden');
    lockBackgroundScroll(modal);

    if (!shouldAnimateModal()) {
      modal.classList.add('is-open');
      modal.classList.remove('is-transitioning');
      modal.dataset.dnModalState = 'open';
      focusModalDialog(modal, dialog);
      return;
    }

    const frame = window.requestAnimationFrame(() => {
      openingFrames.delete(modal);
      if (!modal.isConnected || modal.dataset.dnModalState !== 'opening') return;

      modal.classList.add('is-open');
      modal.dataset.dnModalState = 'open';
      focusModalDialog(modal, dialog);

      afterDialogTransition(modal, () => {
        if (modal.dataset.dnModalState === 'open') {
          modal.classList.remove('is-transitioning');
        }
      });
    });
    openingFrames.set(modal, frame);
  }

  function finishModalClose(modal, trigger, restoreFocus) {
    clearTransitionWait(modal);
    cancelOpeningFrame(modal);
    modal.classList.remove('is-open', 'is-transitioning');
    modal.dataset.dnModalState = 'closed';
    modal.setAttribute('aria-hidden', 'true');

    if (modal === activeModal) {
      activeModal = null;
      activeDialog = null;
      activeTrigger = null;
    }

    if (!getOpenModal()) {
      unlockBackgroundScroll();
    }

    if (restoreFocus && trigger?.isConnected) {
      trigger.focus({ preventScroll: true });
    }
  }

  function closeModal(restoreFocus = true, modalOverride = null, immediate = false) {
    const modal = modalOverride || getOpenModal();
    if (!modal) return;

    const modalState = modal.dataset.dnModalState || 'closed';
    const trigger = activeTrigger;
    if (modalState === 'closed') return;
    if (modalState === 'closing') {
      if (immediate) finishModalClose(modal, trigger, restoreFocus);
      return;
    }

    cancelOpeningFrame(modal);
    clearTransitionWait(modal);
    modal.dataset.dnModalState = 'closing';
    modal.classList.add('is-transitioning');
    modal.classList.remove('is-open');

    if (immediate || !shouldAnimateModal()) {
      finishModalClose(modal, trigger, restoreFocus);
      return;
    }

    afterDialogTransition(modal, () => finishModalClose(modal, trigger, restoreFocus));
  }

  function handleDocumentClick(event) {
    const contactAnchor = closestElement(event.target, selectors.contact);
    if (contactAnchor?.closest(selectors.modal)) {
      return;
    }

    const tabButton = closestElement(event.target, selectors.tab);
    if (tabButton?.closest(selectors.modal)) {
      event.preventDefault();
      event.stopPropagation();
      setActiveModalTab(
        getModalFromControl(tabButton),
        tabButton.dataset.dnSizeTab,
        false
      );
      return;
    }

    const unitButton = closestElement(event.target, selectors.unit);
    if (unitButton?.closest(selectors.modal)) {
      event.preventDefault();
      event.stopPropagation();
      const modal = getModalFromControl(unitButton);
      prepareModal(modal);
      setMeasurementUnit(modal, unitButton.dataset.sizeUnit || unitButton.dataset.dnSizeUnit);
      return;
    }

    const closeControl = closestElement(event.target, selectors.close);
    if (closeControl) {
      event.preventDefault();
      event.stopPropagation();
      closeModal(true, getModalFromControl(closeControl));
      return;
    }

    const overlay = closestElement(event.target, selectors.overlay);
    if (overlay && event.target === overlay) {
      event.preventDefault();
      event.stopPropagation();
      closeModal(true, getModalFromControl(overlay));
      return;
    }

    const opener = closestElement(event.target, selectors.open);
    if (opener) {
      event.preventDefault();
      event.stopPropagation();
      openModal(opener);
    }
  }

  function handleDocumentKeydown(event) {
    const openModalElement = getOpenModal();
    if (!openModalElement) return;

    const focusedTab = closestElement(event.target, selectors.tab);
    if (focusedTab?.closest(selectors.modal)) {
      const tabs = Array.from(openModalElement.querySelectorAll(selectors.tab));
      const currentIndex = tabs.indexOf(focusedTab);
      let nextIndex = currentIndex;

      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
        nextIndex = (currentIndex + 1) % tabs.length;
      } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
        nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
      } else if (event.key === 'Home') {
        nextIndex = 0;
      } else if (event.key === 'End') {
        nextIndex = tabs.length - 1;
      } else {
        nextIndex = -1;
      }

      if (nextIndex !== -1 && tabs[nextIndex]) {
        event.preventDefault();
        setActiveModalTab(
          openModalElement,
          tabs[nextIndex].dataset.dnSizeTab,
          true
        );
        return;
      }
    }

    const focusedClose = closestElement(document.activeElement, selectors.close);
    if (focusedClose && (event.key === 'Enter' || event.key === ' ' || event.key === 'Space' || event.key === 'Spacebar')) {
      event.preventDefault();
      closeModal(true, getModalFromControl(focusedClose));
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      closeModal(true, openModalElement);
      return;
    }

    const dialog = activeDialog?.isConnected ? activeDialog : getDialog(openModalElement);
    if (event.key !== 'Tab' || !dialog) return;

    const focusable = getFocusableElements(dialog);
    if (!focusable.length) {
      event.preventDefault();
      dialog.focus({ preventScroll: true });
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (!dialog.contains(document.activeElement)) {
      event.preventDefault();
      (event.shiftKey ? last : first).focus({ preventScroll: true });
    } else if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus({ preventScroll: true });
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus({ preventScroll: true });
    }
  }

  function refreshFromVariantHtml(payload) {
    const html = payload?.data?.html;
    const sectionId = payload?.data?.sectionId;
    if (!html || !sectionId) return;

    const measurementSelector = `[data-size-measurements="${sectionId}"], [data-dn-size-measurements="${sectionId}"]`;
    const measurementDestination = document.querySelector(measurementSelector);
    if (measurementDestination) removeModalForWrapper(measurementDestination);

    [
      measurementSelector,
      `[data-dn-product-overview="${sectionId}"]`,
      `[data-dn-product-condition="${sectionId}"]`,
      `[data-dn-condition-content="${sectionId}"]`
    ].forEach((selector) => {
      const source = html.querySelector?.(selector);
      const destination = document.querySelector(selector);

      if (source && destination) {
        const restoreConditionFocus = destination.hasAttribute('data-dn-condition-content') && destination.contains(document.activeElement);
        if (source.matches(selectors.wrapper)) prepareModal(source);
        destination.replaceWith(source);
        if (restoreConditionFocus) {
          const close = source.closest('dialog')?.querySelector('[data-dn-policy-close]');
          if (window.DripnordProductFocus) window.DripnordProductFocus.focus(close);
          else close?.focus({ preventScroll: true });
        }
      } else if (source && !destination) {
        const liveProductInfo = document.querySelector(
          `product-info[data-section="${sectionId}"]`
        );
        const slot = source.matches(selectors.wrapper)
          ? liveProductInfo?.querySelector('[data-dn-measurement-slot]')
          : source.hasAttribute('data-dn-product-condition')
            ? liveProductInfo?.querySelector('[data-dn-condition-slot]')
            : source.hasAttribute('data-dn-condition-content')
              ? liveProductInfo?.querySelector(`[data-dn-condition-dialog="${sectionId}"]`)
              : liveProductInfo?.querySelector('[data-dn-details-slot]');
        slot?.appendChild(source);
      } else if (!source && destination) {
        destination.remove();
      }
    });

    initialiseProductInformation(document);
  }

  function initialiseProductInformation(root = document) {
    try {
      placeProductInformationHierarchy(root);
      prepareAllModals(root);
      mountAllModals(root);
    } catch (error) {
      console.warn('DRIPNORD product information placement skipped.', error);
    }
  }

  document.addEventListener('click', handleDocumentClick, true);
  document.addEventListener('keydown', handleDocumentKeydown, true);

  document.addEventListener('shopify:section:load', (event) => initialiseProductInformation(event.target));
  document.addEventListener('shopify:section:select', (event) => initialiseProductInformation(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const wrappers = [];
    if (event.target?.matches?.(selectors.wrapper)) wrappers.push(event.target);
    event.target?.querySelectorAll?.(selectors.wrapper).forEach((wrapper) => wrappers.push(wrapper));
    wrappers.forEach(removeModalForWrapper);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialiseProductInformation());
  } else {
    initialiseProductInformation();
  }

  try {
    if (
      typeof subscribe === 'function' &&
      typeof PUB_SUB_EVENTS !== 'undefined' &&
      PUB_SUB_EVENTS.variantChange
    ) {
      subscribe(PUB_SUB_EVENTS.variantChange, refreshFromVariantHtml);
    }
  } catch (error) {
    console.warn('DRIPNORD Size & Measurements variant refresh skipped.', error);
  }
})();
